import { Link, useLocation } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  FileText,
  BarChart3,
  Bell,
  Settings,
  Shield,
  User,
  Users,
  CreditCard,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ScrollText,
} from 'lucide-react';
import { Button } from './button';

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
  onLogout?: () => void;
  user?: { name: string; email: string } | null;
}

const navItems = [
  { to: '/projects', icon: LayoutDashboard, label: 'Projects' },
  { to: '/logs', icon: FileText, label: 'Logs' },
  { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  { to: '/alerts', icon: Bell, label: 'Alerts' },
];

const settingsItems = [
  { to: '/admin', icon: Users, label: 'Team' },
  { to: '/admin/billing', icon: CreditCard, label: 'Billing' },
  { to: '/admin/audit', icon: ScrollText, label: 'Audit' },
  { to: '/profile', icon: User, label: 'Profile' },
  { to: '/security', icon: Shield, label: 'Security' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

export function Sidebar({ collapsed = false, onToggle, onLogout, user }: SidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;

  const isActive = (path: string) => {
    if (path === '/admin') return pathname === '/admin';
    return pathname.startsWith(path);
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen border-r border-border bg-card transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="flex h-full flex-col">
        {/* Logo */}
        <div className="flex h-14 items-center border-b border-border px-4">
          <Link to="/projects" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-none bg-primary">
              <span className="text-lg font-bold text-primary-foreground">F</span>
            </div>
            {!collapsed && (
              <span className="text-lg font-semibold text-foreground">Frolic</span>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          <div className="mb-2">
            {!collapsed && (
              <span className="px-3 text-xs font-medium uppercase text-muted-foreground">
                Main
              </span>
            )}
          </div>
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={cn(
                'flex items-center gap-3 rounded-none px-3 py-2 text-sm font-medium transition-colors',
                isActive(item.to)
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span>{item.label}</span>
            </Link>
          ))}

          <div className="my-4 border-t border-border" />

          <div className="mb-2">
            {!collapsed && (
              <span className="px-3 text-xs font-medium uppercase text-muted-foreground">
                Settings
              </span>
            )}
          </div>
          {settingsItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={cn(
                'flex items-center gap-3 rounded-none px-3 py-2 text-sm font-medium transition-colors',
                isActive(item.to)
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground'
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* User section */}
        <div className="border-t border-border p-2">
          {user && !collapsed && (
            <div className="mb-2 rounded-none bg-secondary/50 p-3">
              <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            </div>
          )}
          <Button
            variant="ghost"
            className={cn(
              'w-full justify-start gap-3 text-muted-foreground hover:text-destructive',
              collapsed && 'justify-center px-0'
            )}
            onClick={onLogout}
          >
            <LogOut className="h-5 w-5" />
            {!collapsed && <span>Sign Out</span>}
          </Button>
        </div>

        {/* Collapse toggle */}
        <button
          onClick={onToggle}
          className="absolute -right-3 top-20 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-foreground"
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>
    </aside>
  );
}
